import { isDev } from "./env";
import { initLogger, log as evlog } from "evlog";

// Import Sentry conditionnellement pour éviter les erreurs en dev
let Sentry: typeof import("@sentry/react") | null = null;
try {
  // Use dynamic import for better compatibility with browser environments
  if (typeof globalThis.require !== "undefined") {
    Sentry = (globalThis as any).require("@sentry/react");
  }
} catch (e) {
  // Sentry non installé en dev
}

type LogLevel = "debug" | "info" | "warn" | "error";
type LogCategory =
  | "calendar"
  | "performance"
  | "auth"
  | "api"
  | "conversation"
  | "poll"
  | "vote"
  | "analytics"
  | "quota"
  | "dashboard"
  | "security"
  | "gdpr"
  | "groups"
  | "feedback"
  | "general"
  | "aggregator"
  | "temporaryReservation"
  | "userPreferences"
  | "support"
  | "featureRequest"
  | "seo";

interface LogEntry {
  id: string;
  startTime: number;
  category: string;
}

interface LogConfig {
  enableProduction: boolean;
  minLevel: LogLevel;
  silentCategories: LogCategory[];
  enableMonitoring: boolean;
}

class Logger {
  private activeTimers = new Map<string, LogEntry>();
  private isDev = isDev();
  private config: LogConfig = {
    enableProduction: false,
    minLevel: "debug",
    silentCategories: ["calendar", "performance"],
    enableMonitoring: false,
  };

  constructor() {
    initLogger({
      env: {
        service: "doodates-webapp",
        environment: isDev() ? "development" : "production",
      },
      pretty: isDev(),
    });
  }

  private generateUniqueId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  time(message: string, category: string = "general"): string {
    if (!this.isDev) return "";

    const id = this.generateUniqueId();
    const emoji = this.getCategoryEmoji(category);

    this.activeTimers.set(id, {
      id,
      startTime: performance.now(),
      category,
    });

    // Désactiver complètement les logs Calendar pour éviter le spam
    if (category === "calendar") {
      return id; // Retourner l'ID mais pas de log
    }

    console.time(`${emoji} ${message}`);
    return id;
  }

  timeEnd(timerId: string, message?: string): number {
    if (!this.isDev) return 0;

    const entry = this.activeTimers.get(timerId);
    if (!entry) return 0;

    const duration = performance.now() - entry.startTime;
    const emoji = this.getCategoryEmoji(entry.category);
    const finalMessage = message || `${entry.category} operation`;

    // Désactiver complètement les logs Calendar
    if (entry.category === "calendar") {
      this.activeTimers.delete(timerId);
      return duration; // Pas de console.timeEnd
    }

    console.timeEnd(`${emoji} ${finalMessage}`);
    this.activeTimers.delete(timerId);
    return duration;
  }

  // Méthodes principales pour remplacer console.log
  debug(
    message: string,
    category: LogCategory = "general",
    data?: unknown,
  ): void {
    this.logWithLevel("debug", message, category, data);
  }

  info(
    message: string,
    category: LogCategory = "general",
    data?: unknown,
  ): void {
    this.logWithLevel("info", message, category, data);
  }

  warn(
    message: string,
    category: LogCategory = "general",
    data?: unknown,
  ): void {
    this.logWithLevel("warn", message, category, data);
  }

  error(
    message: string,
    category: LogCategory = "general",
    error?: unknown,
  ): void {
    this.logWithLevel("error", message, category, error);

    // En production, envoyer au service de monitoring
    if (!this.isDev && this.config.enableMonitoring) {
      this.sendToMonitoring("error", message, category, error);
    }
  }

  // Méthode legacy pour compatibilité
  log(
    message: string,
    category: LogCategory = "general",
    data?: unknown,
  ): void {
    this.info(message, category, data);
  }

  private logWithLevel(
    level: LogLevel,
    message: string,
    category: LogCategory,
    data?: unknown,
  ): void {
    // En production, ne logger que si explicitement activé
    if (!this.isDev && !this.config.enableProduction) return;

    // Catégories silencieuses
    if (this.config.silentCategories.includes(category)) return;

    // Filtrage par niveau
    const levels: LogLevel[] = ["debug", "info", "warn", "error"];
    const currentLevelIndex = levels.indexOf(level);
    const minLevelIndex = levels.indexOf(this.config.minLevel);
    if (currentLevelIndex < minLevelIndex) return;

    // Structured logging via evlog
    const logData = {
      category,
      message,
      ...(data && { data }),
    };

    switch (level) {
      case "debug":
        evlog.debug(logData);
        break;
      case "info":
        evlog.info(logData);
        break;
      case "warn":
        evlog.warn(logData);
        break;
      case "error":
        evlog.error(logData);
        break;
    }

    // Keep legacy console logs in dev for visual comfort if not using evlog's pretty print
    // (though evlog.info/debug/etc already log to console properly formatted)
  }

  private getLevelEmoji(_level: LogLevel): string {
    const emojis: Record<LogLevel, string> = {
      debug: "🐛",
      info: "ℹ️",
      warn: "⚠️",
      error: "❌",
    };
    return emojis[_level];
  }

  private getCategoryEmoji(category: string): string {
    const emojis: Record<string, string> = {
      calendar: "📅",
      performance: "⚡",
      auth: "🔐",
      api: "🌐",
      analytics: "📊",
      quota: "💳",
      dashboard: "📊",
      security: "🔒",
      error: "❌",
      success: "✅",
      general: "ℹ️",
      support: "🎧",
    };
    return emojis[category] || "ℹ️";
  }

  // Configuration du logger
  configure(config: Partial<LogConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // Activer/désactiver une catégorie
  silenceCategory(category: LogCategory, silent = true): void {
    if (silent && !this.config.silentCategories.includes(category)) {
      this.config.silentCategories.push(category);
    } else if (!silent) {
      this.config.silentCategories = this.config.silentCategories.filter(
        (c) => c !== category,
      );
    }
  }

  // Envoyer les erreurs critiques au service de monitoring (production)
  private sendToMonitoring(
    level: LogLevel,
    message: string,
    category: string,
    data?: unknown,
  ): void {
    // Intégration Sentry si disponible
    if (Sentry && !this.isDev) {
      try {
        if (level === "error") {
          Sentry.captureException(new Error(message), {
            level: "error",
            tags: { category },
            extra: { data },
          });
        } else if (level === "warn") {
          Sentry.captureMessage(message, {
            level: "warning",
            tags: { category },
            extra: { data },
          });
        }
      } catch (sentryError) {
        // Éviter les erreurs récursives si Sentry échoue
        console.error("Sentry logging failed:", sentryError);
      }
    }

    // Stockage local pour debug (backup)
    try {
      const logEntry = {
        timestamp: new Date().toISOString(),
        level,
        message,
        category,
        data: data ? JSON.stringify(data) : undefined,
        userAgent:
          typeof window !== "undefined" ? navigator.userAgent : "server",
        url: typeof window !== "undefined" ? window.location.href : "server",
      };

      if (typeof window !== "undefined") {
        const logs = JSON.parse(
          localStorage.getItem("doodates-error-logs") || "[]",
        );
        logs.push(logEntry);

        // Garder seulement les 50 dernières erreurs
        if (logs.length > 50) {
          logs.shift();
        }

        localStorage.setItem("doodates-error-logs", JSON.stringify(logs));
      }
    } catch (e) {
      // Ignorer les erreurs de stockage
    }
  }

  // Récupérer les logs pour debug
  getStoredLogs(): unknown[] {
    try {
      return JSON.parse(localStorage.getItem("doodates-error-logs") || "[]");
    } catch {
      return [];
    }
  }

  // Nettoyer les timers orphelins (utile pour le HMR)
  cleanup(): void {
    this.activeTimers.clear();
  }

  // Nettoyer les logs stockés
  clearStoredLogs(): void {
    localStorage.removeItem("doodates-error-logs");
  }
}

export const logger = new Logger();

// Exposition globale pour débogage
if (typeof window !== "undefined") {
  (window as Window & { dooLogger?: typeof logger }).dooLogger = logger;
}
